/**
 * `/admin` — staff org overview.
 *
 * Searchable, keyset-paginated table of organisations; each row deep-links to
 * the reconciliation detail and shows a 14-day credit-spend sparkline plus
 * its unit economics (cost to serve / revenue / margin over the selected
 * window, computed server-side from live usage × the DB rate card). Above the
 * table: the all-time running tally and window totals — revenue, cost, profit,
 * and what trials are costing. A founding-partner invite form and the editable
 * rate card sit behind header toggles. Gated by the parent `_app/admin`
 * AdminGate.
 */
import { InviteLink } from '@/components/admin/InviteLink';
import { Sparkline } from '@/components/primitives/Sparkline';
import { Island } from '@/components/shell/Island';
import {
  type AdminOrgListItem,
  type AdminUnitEconomicsOrg,
  type AdminUnitEconomicsResponse,
  useAdminOrgsInfinite,
  useAdminUnitEconomics,
  useInviteOrgMutation,
  useUpdateUnitEconomicsRates,
} from '@/lib/queries/admin';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/admin/')({
  component: AdminOrgsPage,
});

const WINDOWS = [7, 30, 90] as const;

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// USD with adaptive precision: whole dollars read at 2dp; sub-dollar COGS
// (an org's month of RUM can be $0.0006) needs 4dp to not render as $0.00.
function fmtUsd(n: number): string {
  const abs = Math.abs(n);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: abs > 0 && abs < 1 ? 4 : 2,
  });
}

function fmtPct(n: number | null): string {
  return n === null ? '—' : `${n.toFixed(1)}%`;
}

function PlanBadge({ plan }: { plan: string | null }): ReactElement {
  if (!plan) return <span className="text-[var(--color-mute)]">—</span>;
  return (
    <span className="rounded bg-[var(--color-paper-alt)] px-1.5 py-0.5 text-xs capitalize">
      {plan}
    </span>
  );
}

function TrialBadge({ status }: { status: AdminUnitEconomicsOrg['trial_status'] }): ReactElement {
  if (!status) return <span className="text-[var(--color-mute)]">—</span>;
  const styles: Record<NonNullable<typeof status>, string> = {
    active: 'bg-[var(--color-accent-brand)]/10 text-[var(--color-accent-brand)]',
    converted:
      'bg-[var(--color-success-soft,rgba(14,159,110,0.12))] text-[var(--color-success,#0e9f6e)]',
    lapsed: 'bg-[var(--color-paper-alt)] text-[var(--color-mute)]',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${styles[status]}`}>
      {status}
    </span>
  );
}

function SparklineCell({ data }: { data: number[] }): ReactElement {
  // All-zero → a dash rather than a flat line (avoids the primitive's
  // degenerate min==max case and reads clearly as "no activity").
  if (!data.some((v) => v > 0)) {
    return <span className="text-[10px] text-[var(--color-mute)]">no activity</span>;
  }
  return (
    <Sparkline
      data={data.map((y, x) => ({ x, y }))}
      width={104}
      height={24}
      ariaLabel="Credit spend, last 14 days"
    />
  );
}

const compactCount = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function EventsCell({ data }: { data: number[] | null }): ReactElement {
  // Null is a Tinybird outage, not an idle org — saying "no traffic" here
  // would send staff chasing a pixel that is in fact fine.
  if (data === null) {
    return <span className="text-[10px] text-[var(--color-mute)]">unavailable</span>;
  }
  const total = data.reduce((a, b) => a + b, 0);
  if (total === 0) {
    return <span className="text-[10px] text-[var(--color-mute)]">no traffic</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <Sparkline
        data={data.map((y, x) => ({ x, y }))}
        width={104}
        height={24}
        ariaLabel="Pixel events, last 14 days"
      />
      <span className="text-[10px] text-[var(--color-mute)] tabular-nums">
        {compactCount.format(total)}
      </span>
    </div>
  );
}

/**
 * Editable rate card. Edits are held locally (keyed `uc:<metric>` /
 * `pp:<plan>`) and only the changed rows are PATCHed — the server rejects
 * unknown keys atomically, so a partial rate card can't happen.
 */
function RateCardEditor({ rates }: { rates: AdminUnitEconomicsResponse['rates'] }): ReactElement {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const update = useUpdateUnitEconomicsRates();

  const editedCosts = rates.unit_costs
    .filter(
      (r) =>
        edits[`uc:${r.metric}`] !== undefined &&
        Number(edits[`uc:${r.metric}`]) !== r.unit_cost_usd,
    )
    .map((r) => ({ metric: r.metric, unit_cost_usd: Number(edits[`uc:${r.metric}`]) }));
  const editedPrices = rates.plan_prices
    .filter(
      (r) =>
        edits[`pp:${r.plan}`] !== undefined &&
        Number(edits[`pp:${r.plan}`]) !== r.monthly_price_usd,
    )
    .map((r) => ({ plan: r.plan, monthly_price_usd: Number(edits[`pp:${r.plan}`]) }));
  const dirty = editedCosts.length + editedPrices.length > 0;
  const valid = [
    ...editedCosts.map((e) => e.unit_cost_usd),
    ...editedPrices.map((e) => e.monthly_price_usd),
  ].every((n) => Number.isFinite(n) && n >= 0);

  const inputCls =
    'h-7 w-32 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-right text-xs tabular-nums outline-none focus:border-[var(--color-line-strong)]';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!dirty || !valid || update.isPending) return;
        update.mutate(
          {
            ...(editedCosts.length ? { unit_costs: editedCosts } : {}),
            ...(editedPrices.length ? { plan_prices: editedPrices } : {}),
          },
          { onSuccess: () => setEdits({}) },
        );
      }}
      className="mb-4 flex flex-col gap-4 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] p-3"
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <h3 className="mb-2 text-xs uppercase tracking-wide text-[var(--color-mute)]">
            Unit costs (USD per unit)
          </h3>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {rates.unit_costs.map((r) => (
                <tr key={r.metric} className="border-b border-[var(--color-line-soft)]">
                  <td className="py-2 pr-3 align-top">
                    <div className="font-mono text-xs">{r.metric}</div>
                    <div className="mt-0.5 max-w-md text-[11px] text-[var(--color-mute)]">
                      {r.description}
                    </div>
                  </td>
                  <td className="py-2 text-right align-top">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={edits[`uc:${r.metric}`] ?? String(r.unit_cost_usd)}
                      onChange={(e) =>
                        setEdits((s) => ({ ...s, [`uc:${r.metric}`]: e.target.value }))
                      }
                      className={inputCls}
                      aria-label={`Unit cost for ${r.metric}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3 className="mb-2 text-xs uppercase tracking-wide text-[var(--color-mute)]">
            Plan prices (USD per month)
          </h3>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {rates.plan_prices.map((r) => (
                <tr key={r.plan} className="border-b border-[var(--color-line-soft)]">
                  <td className="py-2 pr-3 capitalize">{r.plan}</td>
                  <td className="py-2 text-right">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={edits[`pp:${r.plan}`] ?? String(r.monthly_price_usd)}
                      onChange={(e) =>
                        setEdits((s) => ({ ...s, [`pp:${r.plan}`]: e.target.value }))
                      }
                      className={inputCls}
                      aria-label={`Monthly price for ${r.plan}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-[var(--color-mute)]">
            List prices only — negotiated Business pricing isn't modelled. Revenue figures move with
            these immediately.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!dirty || !valid || update.isPending}
          className="h-8 rounded-md bg-[var(--color-ink)] px-3 font-medium text-[var(--color-paper)] text-xs hover:opacity-90 disabled:opacity-40"
        >
          {update.isPending ? 'Saving…' : update.isSuccess && !dirty ? 'Saved ✓' : 'Save rates'}
        </button>
        {dirty ? (
          <button
            type="button"
            onClick={() => setEdits({})}
            className="h-8 rounded-md border border-[var(--color-line)] px-3 text-xs hover:bg-[var(--color-paper-alt)]"
          >
            Discard
          </button>
        ) : null}
        {update.error ? (
          <span className="text-xs text-[var(--color-danger)]">
            {String((update.error as Error)?.message ?? update.error)}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function InvitePartnerForm(): ReactElement {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const invite = useInviteOrgMutation();
  const valid = name.trim().length > 0 && /.+@.+\..+/.test(email.trim());

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || invite.isPending) return;
        invite.mutate(
          { name: name.trim(), email: email.trim() },
          {
            onSuccess: () => {
              setName('');
              setEmail('');
            },
          },
        );
      }}
      className="mb-4 flex flex-wrap items-end gap-2 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] p-3"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">
          Org name
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Inc"
          className="h-8 w-48 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-sm outline-none focus:border-[var(--color-line-strong)]"
        />
      </label>
      <label className="flex flex-1 flex-col gap-1">
        <span className="text-[11px] text-[var(--color-mute)] uppercase tracking-wide">
          Admin email
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="founder@acme.com"
          className="h-8 w-full min-w-56 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-sm outline-none focus:border-[var(--color-line-strong)]"
        />
      </label>
      <button
        type="submit"
        disabled={!valid || invite.isPending}
        className="h-8 rounded-md bg-[var(--color-ink)] px-3 font-medium text-[var(--color-paper)] text-xs hover:opacity-90 disabled:opacity-40"
      >
        {invite.isPending ? 'Inviting…' : 'Create + invite'}
      </button>
      {invite.isSuccess ? (
        <div className="flex w-full flex-col gap-1.5">
          <span className="text-xs text-[var(--color-mute)]">
            Invited {invite.data.invited_email} — org {invite.data.org.slug} created.
          </span>
          {/* Clerk sends the email and gives us no receipt. Showing the link
              means a partner who never got the mail can be unblocked here,
              without anyone opening the Clerk dashboard. */}
          <InviteLink url={invite.data.invitation.url} />
        </div>
      ) : null}
      {invite.error ? (
        <span className="text-xs text-[var(--color-danger)]">
          {String((invite.error as Error)?.message ?? invite.error)}
        </span>
      ) : null}
    </form>
  );
}

function AdminOrgsPage(): ReactElement {
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [days, setDays] = useState<number>(30);
  const q = useAdminOrgsInfinite(search);
  const econQ = useAdminUnitEconomics(days);
  const econ = econQ.data;
  // Economics are fleet-wide in one response; the org list is paginated —
  // join client-side by uuid. A missing entry (e.g. a deleted org) renders —.
  const econByUuid = new Map((econ?.organisations ?? []).map((o) => [o.uuid, o]));
  const rows = q.data?.pages.flatMap((p) => p.organisations) ?? [];

  return (
    <Island
      title="Admin · Organisations"
      search={
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search slug or uuid…"
          className="h-7 w-64 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2.5 text-xs outline-none focus:border-[var(--color-line-strong)]"
        />
      }
      actions={
        <>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-7 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-xs outline-none focus:border-[var(--color-line-strong)]"
            aria-label="Economics window"
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>
                Last {w} days
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowRates((v) => !v)}
            className="h-7 rounded-md border border-[var(--color-line)] px-2.5 text-xs hover:bg-[var(--color-paper-alt)]"
          >
            {showRates ? 'Close rates' : 'Rates'}
          </button>
          <button
            type="button"
            onClick={() => setShowInvite((v) => !v)}
            className="h-7 rounded-md border border-[var(--color-line)] px-2.5 text-xs hover:bg-[var(--color-paper-alt)]"
          >
            {showInvite ? 'Close' : 'Invite founding partner'}
          </button>
        </>
      }
    >
      {showInvite ? <InvitePartnerForm /> : null}
      {showRates && econ ? <RateCardEditor rates={econ.rates} /> : null}

      {econ && !econ.events_available ? (
        <p className="mb-4 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-3 py-2 text-xs text-[var(--color-mute)]">
          Tinybird is unreachable — RUM volume and its cost are excluded from every economics figure
          right now.
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1280px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-line)] text-left text-xs text-[var(--color-mute)]">
              <th className="py-2 pr-4 font-medium">Slug</th>
              <th className="py-2 pr-4 font-medium">Plan</th>
              <th className="py-2 pr-4 font-medium">Trial</th>
              <th className="py-2 pr-4 font-medium">Subscription</th>
              <th className="py-2 pr-4 font-medium">Activity (14d)</th>
              <th className="py-2 pr-4 font-medium">Events (14d)</th>
              <th className="py-2 pr-4 text-right font-medium">Cost ({days}d)</th>
              <th className="py-2 pr-4 text-right font-medium">Revenue ({days}d)</th>
              <th className="py-2 pr-4 text-right font-medium">Margin</th>
              <th className="py-2 pr-4 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o: AdminOrgListItem) => {
              const e = econByUuid.get(o.uuid);
              return (
                <tr
                  key={o.uuid}
                  className="border-b border-[var(--color-line-soft)] hover:bg-[var(--color-paper-alt)]"
                >
                  <td className="py-2 pr-4">
                    <Link
                      to="/admin/$orgUuid"
                      params={{ orgUuid: o.uuid }}
                      className="font-medium text-[var(--color-ink)] hover:underline"
                    >
                      {o.slug}
                      {o.deleted_at ? (
                        <span className="ml-2 rounded bg-[var(--color-danger-soft,var(--color-paper-alt))] px-1.5 py-0.5 text-[10px] text-[var(--color-danger)] uppercase">
                          deleted
                        </span>
                      ) : null}
                      {e?.is_internal ? (
                        <span
                          className="ml-2 rounded bg-[var(--color-paper-alt)] px-1.5 py-0.5 text-[10px] text-[var(--color-mute)] uppercase"
                          title="Internal staff org — costs counted, revenue excluded"
                        >
                          internal
                        </span>
                      ) : null}
                    </Link>
                  </td>
                  <td className="py-2 pr-4">
                    <PlanBadge plan={o.plan} />
                  </td>
                  <td className="py-2 pr-4">
                    {e ? (
                      <TrialBadge status={e.trial_status} />
                    ) : (
                      <span className="text-[var(--color-mute)]">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-xs">
                    {o.has_subscription ? (
                      <span className="text-[var(--color-ink)]">active</span>
                    ) : (
                      <span className="text-[var(--color-mute)]">none</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <SparklineCell data={o.sparkline} />
                  </td>
                  <td className="py-2 pr-4">
                    <EventsCell data={o.events_sparkline} />
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {e ? (
                      <span
                        title={`audits ${fmtUsd(e.cost.audits)} · ai ${fmtUsd(e.cost.ai)} (${compactCount.format(e.usage.llm_tokens)} tokens) · rum ${e.cost.rum === null ? 'n/a' : fmtUsd(e.cost.rum)}`}
                      >
                        {fmtUsd(e.cost.total)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {e ? fmtUsd(e.revenue.total) : '—'}
                  </td>
                  <td
                    className={`py-2 pr-4 text-right tabular-nums ${
                      e && e.margin < 0 ? 'text-[var(--color-danger)]' : ''
                    }`}
                  >
                    {e ? (
                      <span title={`margin ${fmtPct(e.margin_pct)}`}>{fmtUsd(e.margin)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 pr-4 text-xs text-[var(--color-mute)]">
                    {fmtDate(o.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {q.isLoading ? (
          <p className="py-8 text-center text-sm text-[var(--color-mute)]">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--color-mute)]">
            {search.trim() ? 'No organisations match that search.' : 'No organisations.'}
          </p>
        ) : null}
        {q.error ? (
          <p className="py-4 text-center text-sm text-[var(--color-danger)]">
            {String((q.error as Error)?.message ?? q.error)}
          </p>
        ) : null}

        {q.hasNextPage ? (
          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              className="h-8 rounded-md border border-[var(--color-line)] px-3 text-xs hover:bg-[var(--color-paper-alt)] disabled:opacity-50"
            >
              {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>
    </Island>
  );
}
